import { generateKeyBetween } from 'fractional-indexing';
import * as Y from 'yjs';
import type { Migration } from './types.js';
import { deterministicId } from './deterministic-id.js';

/**
 * v1 -> v2: ingredients move from a Y.Array to a keyed Y.Map carrying a fractional index.
 *
 * The Y.Array shape could not express a move. Reordering meant delete-then-insert, so two
 * people dragging the same ingredient at the same time ended up with it listed twice. The
 * keyed shape turns a move into a last-write-wins update of one `order` string, which
 * cannot duplicate.
 *
 * Two properties make this safe to run anywhere, any number of times:
 *
 *   - It is idempotent: a recipe whose ingredients are already a Y.Map is skipped, so the
 *     client running it on open and the cron Worker running it server-side do not fight.
 *   - It is deterministic: ids come from the content and order keys come from a fixed
 *     sequence. Two offline clients build identical maps. Yjs resolves the concurrent
 *     `set('ingredients', ...)` as last-write-wins, discarding one of the two maps — which
 *     is harmless precisely because both contain exactly the same data.
 */
export const ingredientsToKeyedMap: Migration = {
  version: 2,
  name: 'ingredients-to-keyed-map',
  migrate(doc: Y.Doc): void {
    const recipes = doc.getMap('recipes');
    for (const [recipeId, node] of recipes.entries()) {
      if (!(node instanceof Y.Map)) continue;
      const legacy = node.get('ingredients');
      if (!(legacy instanceof Y.Array)) continue;

      const keyed = new Y.Map<unknown>();
      let order: string | null = null;

      for (let index = 0; index < legacy.length; index += 1) {
        const raw = legacy.get(index);
        const plain = (raw instanceof Y.Map ? raw.toJSON() : raw) as Record<string, unknown> | null;
        if (plain == null || typeof plain !== 'object') continue;

        const name = typeof plain.name === 'string' ? plain.name : '';
        const id = deterministicId(recipeId, index, name);
        order = generateKeyBetween(order, null);

        const entry = new Y.Map<unknown>();
        keyed.set(id, entry);
        entry.set('order', order);
        entry.set('groupHeading', asString(plain.groupHeading));
        entry.set('quantity', asString(plain.quantity));
        entry.set('unit', asString(plain.unit));
        entry.set('name', name);
        entry.set('note', asString(plain.note));
      }

      node.set('ingredients', keyed);
    }
  },
};

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}
